import type { CustomDataLayerEvent } from "@/components/analytics/types";
import {
  CUSTOM_EVENT_COMMON_PARAMETER_NAMES as COMMON_PARAMETER_NAMES,
  CUSTOM_EVENT_DEFINITIONS as EVENT_DEFINITIONS,
  CUSTOM_EVENT_PARAMETER_DEFINITIONS as PARAMETER_DEFINITIONS,
} from "./custom-events-catalog.mjs";

/** GA4 custom event parameter として dataLayer に載せる値。 */
type CustomEventParameterValue = string | number | boolean;

/**
 * custom event parameter の軽量 catalog。
 * GA4 へ送る payload key の存在確認と CSV 出力のため、説明だけを持つ。
 */
export type CustomEventParameterDefinition = {
  description: string;
};

/** custom event で使う parameter 名の正本。 */
export const CUSTOM_EVENT_PARAMETER_DEFINITIONS =
  PARAMETER_DEFINITIONS satisfies Record<
    string,
    CustomEventParameterDefinition
  >;

export type CustomEventParameterName =
  keyof typeof CUSTOM_EVENT_PARAMETER_DEFINITIONS;

/** すべての custom event に gateway が自動付与する低カーディナリティ分析軸。 */
export const CUSTOM_EVENT_COMMON_PARAMETER_NAMES =
  COMMON_PARAMETER_NAMES satisfies readonly CustomEventParameterName[];

type CustomEventCommonParameterName =
  (typeof CUSTOM_EVENT_COMMON_PARAMETER_NAMES)[number];

export type CustomEventCommonPayload = Partial<
  Record<
    CustomEventCommonParameterName,
    CustomEventParameterValueFor<CustomEventCommonParameterName>
  >
>;

/**
 * GA4 custom event 1件の契約。
 * ここでは実際に送る event 名と、event 固有の必須 parameter だけを管理する。
 * 共通 parameter は gateway が `CUSTOM_EVENT_COMMON_PARAMETER_NAMES` から自動付与する。
 */
export type CustomEventDefinition = {
  description: string;
  keyEventCandidate?: boolean;
  parameters: readonly CustomEventParameterName[];
};

/** 実装済み custom event catalog。未実装の future candidate はここに置かない。 */
export const CUSTOM_EVENT_DEFINITIONS = EVENT_DEFINITIONS satisfies Record<
  string,
  CustomEventDefinition
>;

export type CustomEventName = keyof typeof CUSTOM_EVENT_DEFINITIONS;

type CustomEventParameterForName<EventName extends CustomEventName> =
  (typeof CUSTOM_EVENT_DEFINITIONS)[EventName]["parameters"][number];

/** eventName に応じて必須 parameter を導出した payload 型。 */
export type CustomEventPayload<EventName extends CustomEventName> = Record<
  CustomEventParameterForName<EventName>,
  CustomEventParameterValueFor<CustomEventParameterForName<EventName>>
>;

type NumericCustomEventParameterName = never;

export type CustomEventParameterValueFor<
  ParameterName extends CustomEventParameterName,
> = ParameterName extends NumericCustomEventParameterName ? number : string;

/** custom event 送出 API の標準入力。 */
export type CustomEventInput<EventName extends CustomEventName> = {
  eventName: EventName;
  payload: CustomEventPayload<EventName>;
};

/** report_exported で export format だけ後から決めるための payload 型。 */
export type ReportExportedPayloadWithoutFormat = Omit<
  CustomEventPayload<"report_exported">,
  "export_format"
>;

/** GraphQL notification type を GA4 用の lowercase 分類へ正規化する。 */
export const notificationTypeForAnalytics = (
  value: string,
): CustomEventParameterValueFor<"notification_type"> =>
  value.toLowerCase() as CustomEventParameterValueFor<"notification_type">;

/**
 * 型付き custom event 入力を dataLayer に push できる payload へ変換する。
 * 共通 parameter は先に敷き、event 固有 payload で上書きできるようにする。
 * 型と catalog の整合性は TypeScript と catalog test で担保し、送信時は UI を壊さないよう軽く整形するだけに留める。
 */
export function customEventToDataLayerEvent<EventName extends CustomEventName>(
  { eventName, payload }: CustomEventInput<EventName>,
  commonPayload: CustomEventCommonPayload = {},
): CustomDataLayerEvent {
  const dataLayerEvent: CustomDataLayerEvent = { event: eventName };
  for (const [parameterName, value] of Object.entries({
    ...commonPayload,
    ...payload,
  })) {
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      dataLayerEvent[parameterName] = value as CustomEventParameterValue;
    }
  }
  return dataLayerEvent;
}
